import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import logo from "@/assets/postflow-logo.png";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", organization_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "login") await login(form.email, form.password);
      else await register(form);
      navigate("/app");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0f172a]">
      {/* Brand side */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 pf-gradient opacity-20 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <img src={logo} alt="PostFlow AI" className="h-14 w-14 rounded-2xl bg-white p-1.5 object-contain" />
          <div>
            <div className="text-white font-display text-2xl font-extrabold">PostFlow AI</div>
            <div className="text-slate-400 text-sm">Crie. Programe. Publique.</div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-white font-display text-4xl font-extrabold leading-tight">
            Todo o seu conteúdo. <span className="pf-gradient-text">Em um só lugar.</span>
          </h1>
          <p className="text-slate-400 mt-4 leading-relaxed">
            Planejamento editorial com IA, aprovação, calendário e publicação para Instagram,
            Facebook, Google, WhatsApp e YouTube — multiempresa e com controle total.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            {["IDEIAS", "CONTEÚDO", "PROGRAMAÇÃO", "RESULTADOS"].map((t) => (
              <span key={t} className="text-xs tracking-widest text-slate-300 border border-slate-700 rounded-full px-3 py-1">{t}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md pf-fade-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src={logo} alt="PostFlow AI" className="h-12 w-12 rounded-xl bg-white p-1 object-contain shadow" />
            <span className="font-display text-xl font-extrabold text-slate-900">PostFlow AI</span>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">
            {mode === "login" ? "Entrar na sua conta" : "Criar sua conta"}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {mode === "login" ? "Acesse seu painel de conteúdo." : "Comece com uma nova organização."}
          </p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="auth-form">
            {mode === "register" && (
              <div>
                <label className="text-sm font-medium text-slate-700">Seu nome</label>
                <input data-testid="auth-name-input" className="pf-input" value={form.name} onChange={upd("name")} required />
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-slate-700">E-mail</label>
              <input data-testid="auth-email-input" type="email" className="pf-input" value={form.email} onChange={upd("email")} required />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Senha</label>
              <input data-testid="auth-password-input" type="password" className="pf-input" value={form.password} onChange={upd("password")} required />
            </div>
            {mode === "register" && (
              <div>
                <label className="text-sm font-medium text-slate-700">Nome da organização (opcional)</label>
                <input data-testid="auth-org-input" className="pf-input" value={form.organization_name} onChange={upd("organization_name")} />
              </div>
            )}
            {error && <div data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="auth-submit" disabled={busy} className="w-full pf-gradient text-white font-semibold rounded-xl py-3 flex items-center justify-center gap-2 transition-all hover:opacity-95 active:scale-[0.98] disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {mode === "login" ? "Entrar" : "Criar conta"}
            </button>
          </form>

          <div className="mt-4 flex items-center justify-between text-sm">
            <button data-testid="auth-toggle" className="text-[#3457d5] font-medium" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
              {mode === "login" ? "Criar uma conta" : "Já tenho conta"}
            </button>
          </div>
        </div>
      </div>
      <style>{`.pf-input{width:100%;margin-top:6px;border:1px solid #e2e8f0;border-radius:12px;padding:10px 14px;font-size:14px;background:#fff;outline:none;transition:border-color .15s, box-shadow .15s}.pf-input:focus{border-color:#3457d5;box-shadow:0 0 0 3px rgba(52,87,213,.12)}`}</style>
    </div>
  );
}
